import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Navbar } from "./navbar";
import { Contact } from "./contact";
import { addToPanier, updateClimatisseur, updateCartItem } from "./action";
import { LikeCommentaire, DislikeCommentaire, AjouterCommentaire, SupprimerCommentaire, toggleFavoris } from "./slice";
import { patchCompte } from "./action";
import {
    X,
    ShoppingCart,
    ArrowLeft,
    Star,
    ThumbsUp,
    ThumbsDown,
    MessageSquare,
    Trash2,
    Clock,
    CheckCircle2,
    CheckCircle,
    Heart
} from "lucide-react";
import "../styles/infoClimatisseur.css";

export function InfoClimatisseur() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const listC = useSelector(state => state.listC) || [];
    const panier = useSelector(state => state.panier) || [];
    const favoris = useSelector(state => state.favoris) || [];
    const comptes = useSelector(state => state.comptes) || [];
    
    const [user, setUser] = useState(null);
    useEffect(() => {
        const loggedUser = localStorage.getItem('user');
        if (loggedUser) {
            setUser(JSON.parse(loggedUser));
        }
    }, []);

    const climatisseur = listC.find(c => c.id == id);
    const cartItemId = location.state?.cartItemId;
    const cartItem = cartItemId ? panier.find(pi => pi.id === cartItemId) : null;

    // Aggregated color map from all items in listC
    const colorMap = React.useMemo(() => {
        const map = {
            'blanc': '#ffffff', 'noir': '#000000', 'noire': '#000000',
            'noir mat': '#222222', 'gris': '#808080', 'gris anthracite': '#2f3e46',
            'argent': '#C0C0C0', 'argenté': '#C0C0C0', 'inox': '#8e9aaf',
            'bleu': '#0000FF', 'rouge': '#FF0000', 'vert': '#008000',
            'or': '#FFD700', 'doré': '#FFD700', 'beige': '#F5F5DC',
            'black': '#000000', 'white': '#ffffff', 'grey': '#808080',
            'gray': '#808080', 'silver': '#C0C0C0', 'blue': '#0000FF',
            'red': '#FF0000', 'green': '#008000', 'gold': '#FFD700'
        };

        listC.forEach(clim => {
            (clim.colors || []).forEach(c => {
                if (typeof c === 'object' && c.color && c.hex) {
                    map[c.color.trim().toLowerCase()] = c.hex;
                }
            });
        });
        return map;
    }, [listC]);

    const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
    const [selectedColor, setSelectedColor] = useState("Standard");

    useEffect(() => {
        if (cartItem) {
            const sIndex = climatisseur?.tailles.findIndex(t => t.taille === cartItem.taille);
            if (sIndex !== -1) setSelectedSizeIndex(sIndex);
            setSelectedColor(cartItem.color || "Standard");
        } else if (climatisseur?.colors && climatisseur.colors.length > 0) {
            const firstColor = climatisseur.colors[0];
            setSelectedColor(typeof firstColor === 'string' ? firstColor : firstColor?.color || "Standard");
        }
    }, [climatisseur, cartItem]);

    const [showCommentForm, setShowCommentForm] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMsg, setToastMsg] = useState("");

    // Comment form state
    const [commentText, setCommentText] = useState("");
    const [commentRating, setCommentRating] = useState(5);

    // Handle scroll lock when modal-like page is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    if (!climatisseur) {
        return (
            <div className="info-modal-overlay">
                <div className="info-modal-content" style={{ justifyContent: 'center', alignItems: 'center', padding: '50px' }}>
                    <h2>Produit non trouvé</h2>
                    <Link to="/" className="cancel-btn">Retour à l'accueil</Link>
                </div>
            </div>
        );
    }

    const currentTaille = climatisseur.tailles[selectedSizeIndex] || climatisseur.tailles[0];

    const handleAddToCart = () => {
        const targetEmail = user ? user.email : null;

        // Find correct image for the selected color
        const currentColorObj = climatisseur.colors?.find(c =>
            (typeof c === 'string' ? c : c.color) === selectedColor
        );
        const displayImage = (typeof currentColorObj === 'object' && currentColorObj?.image)
            ? currentColorObj.image
            : climatisseur.image;

        // Check if an identical item already exists in the cart for this user
        const existingItem = panier.find(item =>
            item.originalId === climatisseur.id &&
            item.taille === currentTaille.taille &&
            item.color === selectedColor &&
            item.prix === currentTaille.prix &&
            item.userEmail === targetEmail
        );

        if (cartItem) {
            // Update specific item (Editing mode from cart)
            const updatedItem = {
                ...cartItem,
                taille: currentTaille.taille,
                prix: currentTaille.prix,
                color: selectedColor,
                image: displayImage,
                userEmail: targetEmail
            };
            dispatch(updateCartItem(updatedItem));
            setToastMsg(`${climatisseur.nomClimatisseur} mis à jour !`);
        } else if (existingItem) {
            // Item already in cart with same options -> increment quantity
            const updatedItem = {
                ...existingItem,
                quantite: (existingItem.quantite || 1) + 1
            };
            dispatch(updateCartItem(updatedItem));
            setToastMsg(`Quantité de ${climatisseur.nomClimatisseur} augmentée !`);
        } else {
            // New item completely
            const newItem = {
                originalId: climatisseur.id,
                nomClimatisseur: climatisseur.nomClimatisseur,
                image: displayImage,
                taille: currentTaille.taille,
                prix: currentTaille.prix,
                quantite: 1,
                checked: true,
                userEmail: targetEmail,
                color: selectedColor
            };
            dispatch(addToPanier(newItem));
            setToastMsg(`${climatisseur.nomClimatisseur} ajouté au panier !`);
        }

        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const handleLike = (comId) => {
        if (!user) {
            alert("Veuillez vous connecter pour aimer un commentaire.");
            return;
        }
        dispatch(LikeCommentaire({ climatiseurId: climatisseur.id, commentaireId: comId, userEmail: user.email }));

        // Sync with backend
        const updatedClim = { ...climatisseur };
        const updatedComments = updatedClim.commantaires.map(com => {
            if (com.id === comId) {
                const likedBy = com.likedBy ? [...com.likedBy] : [];
                const dislikedBy = com.dislikedBy ? [...com.dislikedBy] : [];
                let like = com.like || 0;
                let dislike = com.dislike || 0;

                if (likedBy.includes(user.email)) {
                    likedBy.splice(likedBy.indexOf(user.email), 1);
                    like = Math.max(0, like - 1);
                } else {
                    likedBy.push(user.email);
                    like += 1;
                    if (dislikedBy.includes(user.email)) {
                        dislikedBy.splice(dislikedBy.indexOf(user.email), 1);
                        dislike = Math.max(0, dislike - 1);
                    }
                }
                return { ...com, likedBy, dislikedBy, like, dislike };
            }
            return com;
        });
        dispatch(updateClimatisseur({ ...updatedClim, commantaires: updatedComments }));
    };

    const handleDislike = (comId) => {
        if (!user) {
            alert("Veuillez vous connecter pour voter.");
            return;
        }
        dispatch(DislikeCommentaire({ climatiseurId: climatisseur.id, commentaireId: comId, userEmail: user.email }));

        // Sync with backend
        const updatedClim = { ...climatisseur };
        const updatedComments = updatedClim.commantaires.map(com => {
            if (com.id === comId) {
                const likedBy = com.likedBy ? [...com.likedBy] : [];
                const dislikedBy = com.dislikedBy ? [...com.dislikedBy] : [];
                let like = com.like || 0;
                let dislike = com.dislike || 0;

                if (dislikedBy.includes(user.email)) {
                    dislikedBy.splice(dislikedBy.indexOf(user.email), 1);
                    dislike = Math.max(0, dislike - 1);
                } else {
                    dislikedBy.push(user.email);
                    dislike += 1;
                    if (likedBy.includes(user.email)) {
                        likedBy.splice(likedBy.indexOf(user.email), 1);
                        like = Math.max(0, like - 1);
                    }
                }
                return { ...com, likedBy, dislikedBy, like, dislike };
            }
            return com;
        });
        dispatch(updateClimatisseur({ ...updatedClim, commantaires: updatedComments }));
    };

    const submitComment = (e) => {
        e.preventDefault();
        if (!user) {
            alert("Connectez-vous pour laisser un avis !");
            navigate("/login");
            return;
        }
        if (!commentText.trim()) return;

        const newCommentId = climatisseur.commantaires && climatisseur.commantaires.length > 0
            ? Math.max(...climatisseur.commantaires.map(c => c.id)) + 1
            : 1;

        const commentObj = {
            id: newCommentId,
            username: user.name || user.email.split('@')[0],
            commantaire: commentText,
            avis: `${commentRating} ${commentRating === 1 ? 'étoile' : 'étoiles'}`,
            like: 0,
            dislike: 0,
            likedBy: [],
            dislikedBy: [],
            status: 'en attente',
            date: new Date().toLocaleDateString('fr-FR')
        };

        dispatch(AjouterCommentaire({ climatiseurId: climatisseur.id, commentaire: commentObj }));
        dispatch(updateClimatisseur({ ...climatisseur, commantaires: [...(climatisseur.commantaires || []), commentObj] }));

        setCommentText("");
        setShowCommentForm(false);
        setToastMsg("Merci pour votre avis !");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const handleDeleteComment = (comId) => {
        if (window.confirm("Supprimer votre avis ?")) {
            dispatch(SupprimerCommentaire({ climatiseurId: climatisseur.id, commentaireId: comId }));
            const filtered = climatisseur.commantaires.filter(c => c.id !== comId);
            dispatch(updateClimatisseur({ ...climatisseur, commantaires: filtered }));
        }
    };

    const handleToggleFavoris = () => {
        if (!user) {
            alert("Veuillez vous connecter pour gérer vos favoris.");
            navigate("/login");
            return;
        }

        const isFavorite = favoris.includes(climatisseur.id);
        const newFavoris = isFavorite
            ? favoris.filter(id => id !== climatisseur.id)
            : [...favoris, climatisseur.id];

        dispatch(toggleFavoris(climatisseur.id));
        dispatch(patchCompte({ id: user.id, favoris: newFavoris }));
    };

    const handleClose = () => {
        navigate(-1);
    }

    return (
        <div className="info-modal-overlay" onClick={handleClose}>
            {showToast && (
                <div className="toast-notification">
                    <CheckCircle2 size={20} style={{ marginRight: '10px' }} />
                    {toastMsg}
                </div>
            )}

            <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={handleClose} title="Fermer">
                    <X />
                </button>

                <div className="modal-image-side">
                    {/* Dynamic image based on selected color */}
                    {(() => {
                        const currentColorObj = climatisseur.colors?.find(c =>
                            (typeof c === 'string' ? c : c.color) === selectedColor
                        );
                        const displayImage = (typeof currentColorObj === 'object' && currentColorObj?.image)
                            ? currentColorObj.image
                            : climatisseur.image;

                        return <img src={displayImage} alt={climatisseur.nomClimatisseur} />;
                    })()}
                </div>

                <div className="modal-details-side">
                    <h2>{climatisseur.nomClimatisseur}</h2>
                    <p className="price-tag">{currentTaille.prix.toLocaleString()} MAD</p>

                    <p className="description-text">
                        {climatisseur.description || "Le climatiseur " + climatisseur.nomClimatisseur + " offre une performance de refroidissement rapide et une efficacité énergétique exceptionnelle. Avec sa technologie avancée, il est silencieux et économique."}
                    </p>

                    <div className="option-group">
                        <label>TAILLE DISPONIBLE</label>
                        <select
                            className="modal-select"
                            value={selectedSizeIndex}
                            onChange={(e) => setSelectedSizeIndex(parseInt(e.target.value))}
                        >
                            {climatisseur.tailles.map((t, index) => (
                                <option key={index} value={index}>{t.taille}</option>
                            ))}
                        </select>
                    </div>

                    {climatisseur.colors && climatisseur.colors.length > 0 && (
                        <div className="option-group">
                            <label>COULEURS DISPONIBLES</label>
                            <div className="color-options-container">
                                {climatisseur.colors.map((color, idx) => {
                                    // Safety check: handle if color is an object instead of a string
                                    const colorName = typeof color === 'string' ? color : color?.color || `Color ${idx + 1}`;
                                    const normalized = colorName.trim().toLowerCase();

                                    // Use color.hex if it's an object, otherwise use the mapping or the raw string
                                    const bgColor = colorMap[normalized] || (typeof color === 'object' ? color?.hex : null) || normalized || "#ffffff";

                                    return (
                                        <div
                                            key={idx}
                                            className={`color-square ${selectedColor === colorName ? 'selected' : ''}`}
                                            style={{ backgroundColor: bgColor }}
                                            onClick={() => setSelectedColor(colorName)}
                                            title={colorName}
                                        >
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="option-group" style={{ marginTop: '20px' }}>
                        <label>PROPRIÉTÉS TECHNIQUES</label>
                        <table className="tech-table">
                            <tbody>
                                {currentTaille.proprietesTechniques ? (
                                    currentTaille.proprietesTechniques.map((prop, idx) => (
                                        <tr key={idx}>
                                            <td>{prop.propriete}</td>
                                            <td>{prop.valeur}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <>
                                        <tr><td>puissance</td><td>{climatisseur.puissance || "12000W"}</td></tr>
                                        <tr><td>surface</td><td>{climatisseur.surface || "8-12m²"}</td></tr>
                                        <tr><td>type</td><td>{climatisseur.type || "Inverter"}</td></tr>
                                        <tr><td>categorie</td><td>{climatisseur.categorie || "split"}</td></tr>
                                        <tr><td>Efficacité énergétique</td><td>{climatisseur.efficacite || "A++"}</td></tr>
                                        <tr><td>refrigerant</td><td>{climatisseur.refrigerant || "R32"}</td></tr>
                                        <tr><td>garantie</td><td>{climatisseur.garantie || "5 ans"}</td></tr>
                                        <tr><td>niveau sonore</td><td>{climatisseur.sonore || "19 dB"}</td></tr>
                                        <tr><td>consommation</td><td>{climatisseur.consommation || "850W"}</td></tr>
                                        <tr><td>poids</td><td>{climatisseur.poids || "7.5 kg"}</td></tr>
                                        <tr><td>gaz</td><td>{climatisseur.gaz || "R32"}</td></tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="option-group">
                        <label>DÉTAILS SUR LA TAILLE (DIMENSIONS)</label>
                        <div className="size-details-box">
                            <div className="size-detail-item">
                                <span className="size-detail-key">LONGUEUR :</span>
                                <span className="size-detail-value">{currentTaille.details?.longueur || "68 cm"}</span>
                            </div>
                            <div className="size-detail-item">
                                <span className="size-detail-key">LARGEUR :</span>
                                <span className="size-detail-value">{currentTaille.details?.largeur || "24 cm"}</span>
                            </div>
                            <div className="size-detail-item">
                                <span className="size-detail-key">SOMK :</span>
                                <span className="size-detail-value">{currentTaille.details?.somk || "17 cm"}</span>
                            </div>
                        </div>
                    </div>

                    <div className="comments-section">
                        <div className="comments-header">
                            <h4>COMMENTAIRES</h4>
                            <button className="add-comment-btn" onClick={() => {
                                if (!user) {
                                    alert("Veuillez vous connecter pour donner un avis.");
                                    navigate("/login");
                                    return;
                                }
                                navigate(`/ajouterCommantaire/${climatisseur.id}`);
                            }}>
                                Ajouter
                            </button>
                        </div>

                        <div className="comments-list-simple">
                            {(() => {
                                const validatedComments = (climatisseur.commantaires || []).filter(c => c.status === 'validé');
                                if (validatedComments.length === 0) {
                                    return (
                                        <div className="no-comments-placeholder">
                                            <MessageSquare size={30} />
                                            <p>Soyez le premier à donner votre avis !</p>
                                        </div>
                                    );
                                }
                                return [...validatedComments]
                                    .sort((a, b) => b.id - a.id)
                                    .map((com) => (
                                        <div key={com.id} className="comment-card">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                    <h6>{com.username}</h6>
                                                    <div style={{ display: 'flex', gap: '2px', marginBottom: '8px' }}>
                                                        {[...Array(5)].map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            size={12}
                                                            fill={i < parseInt(com.avis) ? "#ffc107" : "none"}
                                                            color={i < parseInt(com.avis) ? "#ffc107" : "#ccc"}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            {user && (user.name === com.username || user.email.startsWith(com.username)) && (
                                                <button className="btn-icon" style={{ padding: '5px' }} onClick={() => handleDeleteComment(com.id)}>
                                                    <Trash2 size={14} color="#888" />
                                                </button>
                                            )}
                                        </div>
                                        <p className="comment-text">{com.commantaire}</p>
                                        <div className="comment-actions">
                                            <button
                                                className={`reaction-btn ${com.likedBy?.includes(user?.email) ? 'active-like' : ''}`}
                                                onClick={() => handleLike(com.id)}
                                            >
                                                <ThumbsUp size={14} /> <span>{com.like || 0}</span>
                                            </button>
                                            <button
                                                className={`reaction-btn ${com.dislikedBy?.includes(user?.email) ? 'active-dislike' : ''}`}
                                                onClick={() => handleDislike(com.id)}
                                            >
                                                <ThumbsDown size={14} /> <span>{com.dislike || 0}</span>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            })()}
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button className="buy-btn" onClick={handleAddToCart}>
                            <ShoppingCart size={20} style={{ marginRight: '10px' }} />
                            Ajouter au Panier
                        </button>
                        <button className="cancel-btn" onClick={handleClose}>
                            Annuler
                        </button>
                    </div>
                </div>

                {/* Floating Favorite Button */}
                <button 
                    className={`btn-favorite-detail ${favoris.includes(climatisseur.id) ? 'active' : ''}`}
                    onClick={handleToggleFavoris}
                    title={favoris.includes(climatisseur.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                >
                    <Heart size={24} fill={favoris.includes(climatisseur.id) ? "currentColor" : "none"} />
                </button>
            </div>
        </div>
    );
}
